import { useCallback, useMemo, useRef, useState } from 'react';

import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SalaryApiRecentSubmission } from '#/modules/salary/api/types/SalaryApiRecentSubmission';
import { CareerPathChart } from '#/modules/salary/components/CareerPathChart/CareerPathChart';
import { CareerPathChartHeader } from '#/modules/salary/components/CareerPathChartHeader/CareerPathChartHeader';
import { CareerPathHeader } from '#/modules/salary/components/CareerPathHeader/CareerPathHeader';
import { FeedbackBar } from '#/modules/salary/components/FeedbackBar/FeedbackBar';
import { MainChart } from '#/modules/salary/components/MainChart/MainChart';
import { MainChartFooter } from '#/modules/salary/components/MainChartFooter/MainChartFooter';
import { MainChartHeader } from '#/modules/salary/components/MainChartHeader/MainChartHeader';
import { MainChartSidebar } from '#/modules/salary/components/MainChartSidebar/MainChartSidebar';
import { RecentSubmissions } from '#/modules/salary/components/RecentSubmissions/RecentSubmissions';
import { RelatedJobs } from '#/modules/salary/components/RelatedJobs/RelatedJobs';
import { ReportSection } from '#/modules/salary/components/ReportSection/ReportSection';
import { buildCareerPathChart } from '#/modules/salary/helpers/buildCareerPathChart';
import { buildMainChart } from '#/modules/salary/helpers/buildMainChart';
import type { CalculatorReport } from '#/modules/salary/types/CalculatorReport';
import type { EstimatedRange } from '#/modules/salary/types/EstimatedRange';
import type { MainChartData } from '#/modules/salary/types/MainChartData';
import type { SalariesReportFormData } from '#/modules/salary/types/SalariesReportFormData';
import { breakpointValues } from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SalaryExplorePageNoDataHolder } from './SalaryExplorePage.styled';
import { DEFAULT_CHART_DATA } from './defaultChartData';

type Props = {
  initialSearchApiKey: string;
  alreadySubmitted: boolean;
  salaryModelCreated: number;
  recentSubmissions: SalaryApiRecentSubmission[];
  initialSurveyReportFormData: SalariesReportFormData | null;
};

export function SalaryExplorePage({
  initialSearchApiKey,
  alreadySubmitted,
  salaryModelCreated,
  recentSubmissions,
  initialSurveyReportFormData,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const [searchApiKey, setSearchApiKey] = useState(initialSearchApiKey);

  const [data, setData] = useState<MainChartData>();
  const [careerPathData, setCareerPathData] = useState<EstimatedRange[]>([]);

  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  const INITIAL_MAIN_CHART = useMemo(
    () => buildMainChart(i18n, locale, DEFAULT_CHART_DATA),
    [i18n, locale],
  );

  const mainChartRef = useRef<HTMLDivElement>(null);

  const scrollToChart = () => {
    if (window.innerWidth < breakpointValues.md)
      mainChartRef.current?.scrollIntoView();
  };

  const setResults = useCallback(
    (calculatorResult: CalculatorReport, values: SalariesReportFormData) => {
      const { submissionData, estimatedRange } = calculatorResult;

      if (submissionData || estimatedRange.scatterData) {
        const { filtersUsed = {} } = submissionData || {};
        const { xTicks, lineChart, barChart } = buildMainChart(
          i18n,
          locale,
          calculatorResult,
        );
        setData({
          // @ts-expect-error TS(2322): Type 'BarChart | null' is not assignable to type '... Remove this comment to see the full error message
          barChart,
          filtersUsed,
          xTicks,
          // @ts-expect-error TS(2322): Type 'LineChart | null' is not assignable to type ... Remove this comment to see the full error message
          lineChart,
          submittedData: {
            selectedSynonymFamilyId: values.selectedSynonymFamilyId,
            selectedSynonymId: values.selectedSynonymId,
            selectedSynonymLevel: values.selectedSynonymLevel,
            jobDescription: values.jobDescription,
            jobTitleSelected: values.jobTitle,
            jobTitleId: values.jobTitleId,
            city: values.city,
            geoType: values.geoType,
            stateCode: values.stateCode,
            latitude: values.latitude,
            longitude: values.longitude,
            yearsOfExperience: values.yearsOfExperience,
          },
        });
        trackEvent('Salary Calculator Report Displayed', {
          job_title: values.jobTitle,
          city: values.city,
          years_of_experience: values.yearsOfExperience,
          state: values.stateCode,
          user_id: undefined,
          employer_city_bucket: filtersUsed.cityBucket,
          sources: filtersUsed.sources,
          years_of_experience_used: filtersUsed.yearsOfExperience,
        });
      } else {
        // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        setData(null);
      }

      scrollToChart();
    },
    [i18n, locale],
  );

  const showPlaceholderChart = loading || !initialized;
  const { submittedData } = data ?? {};

  return (
    <>
      <ReportSection
        backgroundImage={`url('${getStaticImageSrc('salary-bg.svg', appConfig.idealist.staticImgPath)}')`}
        chart={
          data === null && !loading ? (
            <SalaryExplorePageNoDataHolder>
              <img
                src={getStaticImageSrc(
                  'calculator.svg',
                  appConfig.idealist.staticImgPath,
                )}
                height={430}
                width={430}
                alt="calculator"
              />
            </SalaryExplorePageNoDataHolder>
          ) : (
            <MainChart
              // @ts-expect-error TS(2322): Type '{ xTicks: (string | number)[]; lineChart: Li... Remove this comment to see the full error message
              data={showPlaceholderChart ? INITIAL_MAIN_CHART : data}
              setData={setData}
              initialized={initialized}
              loading={loading}
            />
          )
        }
        chartFooter={
          <MainChartFooter
            // @ts-expect-error TS(2322): Type '{ xTicks: (string | number)[]; lineChart: Li... Remove this comment to see the full error message
            data={showPlaceholderChart ? INITIAL_MAIN_CHART : data}
            initialized={initialized}
            loading={loading}
          />
        }
        chartHeader={
          <>
            <div ref={mainChartRef} />
            <MainChartHeader
              // @ts-expect-error TS(2322): Type 'MainChartData | undefined' is not assignable... Remove this comment to see the full error message
              data={data}
              initialized={initialized}
              loading={loading}
            />
          </>
        }
        sidebar={
          <MainChartSidebar
            formAnalyticsTitle="[main] Salary report page - main chart"
            formUsageContext="page"
            setInitialized={setInitialized}
            setLoading={setLoading}
            setResults={setResults}
            setCareerPathData={setCareerPathData}
            searchApiKey={searchApiKey}
            setSearchApiKey={setSearchApiKey}
            initialSurveyReportFormData={initialSurveyReportFormData}
          />
        }
      />

      {initialized && data && careerPathData.length > 0 ? (
        <ReportSection
          chartHasBorderTop
          chartHeader={
            <CareerPathChartHeader
              formAnalyticsTitle="[main] Salary report page - career path chart"
              formUsageContext="page"
              careerPathData={careerPathData}
              setCareerPathData={setCareerPathData}
              searchApiKey={searchApiKey ?? null}
              setSearchApiKey={setSearchApiKey}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              city={submittedData.city}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              stateCode={submittedData.stateCode}
              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              yearsOfExperience={submittedData.yearsOfExperience}
              salaryModelCreated={salaryModelCreated}
              // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
              selectedSynonymId={submittedData.selectedSynonymId}
              analyticsData={{
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                job_title_main: submittedData.jobTitleSelected,
                job_titles_displayed: careerPathData.map((estimatedRange) => ({
                  synonym_name: estimatedRange.synonymName,
                  standard_name: estimatedRange.jobTitleName,
                })),
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                city: submittedData?.city,
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                employer_city_bucket: data.filtersUsed?.cityBucket,
                // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
                state: data.submittedData?.stateCode,
                // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
                years_of_experience: data.submittedData?.yearsOfExperience,
                // @ts-expect-error TS(2322): Type '[number, number] | undefined' is not assigna... Remove this comment to see the full error message
                years_of_experience_used: data.filtersUsed?.yearsOfExperience,
              }}
            />
          }
          chart={
            <CareerPathChart
              analyticsData={{
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                job_title_main: submittedData.jobTitleSelected,
                job_titles_displayed: careerPathData.map((estimatedRange) => ({
                  synonym_name: estimatedRange.synonymName,
                  standard_name: estimatedRange.jobTitleName,
                })),
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                city: submittedData?.city,
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                employer_city_bucket: data.filtersUsed?.cityBucket,
                // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                state: data.submittedData?.stateCode,
                // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
                years_of_experience: data.submittedData?.yearsOfExperience,
                // @ts-expect-error TS(2322): Type '[number, number] | undefined' is not assigna... Remove this comment to see the full error message
                years_of_experience_used: data.filtersUsed?.yearsOfExperience,
              }}
              // @ts-expect-error TS(2345): Argument of type 'EstimatedRange[]' is not assigna... Remove this comment to see the full error message
              data={buildCareerPathChart(careerPathData)}
              loading={loading}
            />
          }
          header={<CareerPathHeader data={data} />}
        />
      ) : null}

      {initialized && submittedData ? (
        <FeedbackBar submittedData={submittedData} />
      ) : null}

      <RelatedJobs
        headline={initialized ? i18n('Related Jobs') : i18n('Recent Jobs')}
        submittedData={submittedData}
      />

      <RecentSubmissions
        recentSubmissions={recentSubmissions}
        alreadySubmitted={alreadySubmitted}
      />
    </>
  );
}
